//@flow
import api from '../Utils/apiBuilder';
import Constants from '../constants';
import type { AddressFields, AddressObject } from '../Stores/Addresses/addressModel';
import UrlData from '../Utils/url-data';
import CookiesData from '../Utils/cookies-data';
import Heap from '../Utils/heap-interface';
import HeapActions from '../Actions/heap';
import TrackJs from '../Utils/trackjs-interface';
import axios from 'axios';

type CreateCustomerRequest = {
  email:string,
  firstname?:?string,
  lastname?:?string,
  send_set_email?:boolean,
  add_giftcode?:string,
  password:string,
  how_heard_about_nb?:string,
  grant_type:'password',
};

export default {
  getCustomer() {
    return api('customers').softAuth().get().dispatch(Constants.GET_CUSTOMER_SUCCESS);
  },

  redeemGiftCard(customerId:string, giftCardCode:string) {
    return api('customers', customerId, 'redeemGiftCode')
      .post({ gift_code: giftCardCode })
      .dispatch(Constants.REDEEM_GIFTCODE_SUCCESS);
  },

  createCustomer(data:CreateCustomerRequest) {
    data.grant_type = 'password';

    return api('customers').noauth()
      .post(data)
      .dispatch(Constants.CREATE_CUSTOMER_SUCCESS)
      .dispatch(Constants.GET_CUSTOMER_SUCCESS);
  },

  sendPasswordResetEmail(email:string) {
    return api('customers', 'forgotpassword').noauth().post({ email })
      .dispatch(Constants.SEND_PASSWORD_RESET_EMAIL_SUCCESS);
  },

  resetPassword(data:{
    id:string,
    token:string,
    password:string,
  }) {
    return api('customers', data.id, 'resetpassword').noauth().put(data)
      .dispatch(Constants.RESET_PASSWORD_SUCCESS);
  },

  updateCustomerDetails(customerId:string, data:Object) {
    return api('customers', customerId).put(data)
      .dispatch(Constants.UPDATE_CUSTOMER_DETAILS_SUCCESS);
  },

  sendMagicLink(email:string) {
    return api('customers', 'magiclink').noauth().post({email});
  },

  addAddress(customerId:string, address:AddressFields) {
    return this.updateCustomerDetails(customerId, { address });
  },

  updateAddress(customerId:string, addressId:string, address:$Shape<AddressObject>) {
    address.id = addressId;
    return this.updateCustomerDetails(customerId, { address });
  },

  deleteAddress(customerId:string, id:string) {
    const address = { id, action: 'delete' };
    return this.updateCustomerDetails(customerId, { address });
  },

  submitOfficeForm(values:Object, status:string = 'open') {
    const urlBase = UrlData.getEngineApiUrl();

    const nextValues = transformOfficeFormValues(values);

    const config = {
      method: 'post',
      url: `${urlBase}/v1/b2b-leads`,
      data: {
        values: nextValues,
        status,
      },
    };
    return axios(config)
      .then(response => {
        HeapActions.trackSubmitOfficeForm(nextValues);
        return {response};
      })
      .catch(error => {
        const msg = `submitOfficeForm failed; values: ${JSON.stringify(values)}`;
        TrackJs.console.debug(msg);
        // eslint-disable-next-line promise/no-return-wrap
        return Promise.reject({error});
      });
  },

  // NOTE: we use axios for making this request
  // because of unsolved issues in trying to
  // perform request using superagent
  getDelightedTestimonials() {
    const url = "https://d8ddsfj6tapvz.cloudfront.net/testimonials/v1/kUnxL1yE6lX5dljnz51p5vatzmvwaxuu/testimonials";
    const config = {
      method: 'get',
      url,
      headers: {
        accept: '*/*',
      },
    };
    return axios(config);
  },

  submitOfficeReferForm(values:Object) {
    const urlBase = UrlData.getEngineApiUrl();
    const config = {
      method: 'post',
      url: `${urlBase}/v1/b2b-refer`,
      data: values,
    };
    return axios(config)
      .then(response => {
        return {response};
      })
      .catch(error => {
        const msg = `submitOfficeReferForm failed - error: ${JSON.stringify(error)}; values: ${JSON.stringify(values)}`;
        TrackJs.console.debug(msg);
        // eslint-disable-next-line promise/no-return-wrap
        return Promise.reject({error});
      });
  },
};


const OFFICE_FORM_URL_PARAM_KEYS = [
  'promo_code',
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'source',
];

const transformOfficeFormValues = (values:Object) => {
  // Per requirements of sales team,
  // in addition to form data, we need to send
  // heap user id, full_name key/val pair to GTM
  // (so that lead/contact info
  // is created correctly in close.com) and various
  // url param data (for comparing data between
  // heap analytics and close.com);

  // Some of this data may be preserved in the cookie on app
  // load so that query string does not need to be passed in
  // all links as user is navigating site before submitting
  // the office form. Extract that data here and merge with any
  // new query params that may be more recent
  const initialQueryData = CookiesData.getInitialQueryDataObject();

  // Filter for only the key/values we need for form submission
  const cookieQueryData = {};
  Object.keys(initialQueryData).forEach(key => {
    if (OFFICE_FORM_URL_PARAM_KEYS.includes(key)) {
      cookieQueryData[key] = initialQueryData[key];
    }
  });

  const urlValues:Object = OFFICE_FORM_URL_PARAM_KEYS.reduce((acc, key) => {
    return {
      ...acc,
      [key]: UrlData.parseUrlForKey(key) || '',
    };
  }, {});
  urlValues.url = UrlData.getHref();

  const allUrlValues:Object = {};
  // Make sure that values from current url query params do not
  // overwrite query params from cookie if current url query param
  // is empty string and cookie query param has value.
  // Default to empty string if no value available in
  // url or cookie
  Object.keys(urlValues).forEach(key => {
    const urlValue = urlValues[key];
    const cookieValue = cookieQueryData[key];
    if (urlValue) allUrlValues[key] = urlValue;
    else if (cookieValue) allUrlValues[key] = cookieValue;
    else allUrlValues[key] = '';
  });

  const full_name = values.last_name
    ? `${values.first_name} ${values.last_name}`
    : `${values.first_name}`;

  return {
    employee_range: values.employee_range || '',
    first_name: values.first_name || '',
    last_name: values.last_name || '',
    full_name,
    email: values.email,
    phone: values.phone,
    company: values.company || '',
    location_state: values.location_state || '',
    heap_user_id: Heap.getUserId() || '',
    heap_identity: Heap.getIdentity() || '',
    ...allUrlValues,
  };
};
